import { Checkbox, Divider, Drawer, Form, Radio, Select } from "antd"
import React, { FC } from "react"
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { Button } from "components"
import { LoadingRegion } from "components/custom/Loader"
import { Skeleton as AppSkeleton } from "components/new-stuff/ui/Skeleton"
import DateInput from "../setup/components/DateInput"
import { FormInstance } from "antd/lib"
import { useQuery } from "@tanstack/react-query"
import { useGet } from "utils/useFetch"
import { ewaAxiosInstance } from "utils/request"
import {
  IEnrolledEmployeeRes,
  IShiftPeriodsRes,
  IShiftPlansRes,
} from "../typings"

// ─── Types ───────────────────────────────────────────────────────────────────

interface FilterDrawerProps {
  open: boolean
  onClose: () => void
  onReset: () => void
  onApply: (queryString: string) => void
  workplaceId?: string
  companyId?: number
  dutyLocationId: number | null
  departmentId?: number | string
  form: FormInstance
  isLoading: boolean
  filterQuery?: string
}

// ─── Constants ────────────────────────────────────────────────────────────────

const FLAG_OPTIONS = [
  { name: "Manual Clock-ins", value: "Manual Entry", key: "manual_clock-ins" },
  {
    name: "Outside Geofence Clock-ins",
    value: "outside geofence",
    key: "outside_geofence",
  },
  { name: "Late Clock-ins", value: "Late Clock-In", key: "late_clock_ins" },
  {
    name: "Early Departures",
    value: "early clock in",
    key: "early_departures",
  },
  { name: "Overtime", value: "overtime", key: "overtime" },
] as const

const FLAG_MAP: Record<string, keyof FlagState> = {
  "Manual Entry": "manualClockin",
  "outside geofence": "outsideGeofence",
  "Late Clock-In": "lateClockin",
  "early clock in": "earlyClockout",
  overtime: "overtime",
}

interface FlagState {
  manualClockin: string
  outsideGeofence: string
  lateClockin: boolean
  earlyClockout: boolean
  overtime: boolean
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const buildFlagState = (flags: string[]): FlagState => {
  const state: FlagState = {
    manualClockin: "",
    outsideGeofence: "",
    lateClockin: false,
    earlyClockout: false,
    overtime: false,
  }

  flags?.forEach((flag) => {
    const key = FLAG_MAP[flag]
    if (!key) return
    if (key === "manualClockin") state.manualClockin = "manual"
    else if (key === "outsideGeofence") state.outsideGeofence = "outside"
    else (state[key] as boolean) = true
  })

  return state
}

const isCompleteDate = (date?: { dd: string; mm: string; yyyy: string }) =>
  !!(date?.dd && date?.mm && date?.yyyy)

const buildDateParams = (
  dateType: string,
  startDate: { dd: string; mm: string; yyyy: string },
  endDate: { dd: string; mm: string; yyyy: string },
  params: URLSearchParams
): void => {
  if (dateType !== "custom_range") return

  if (!isCompleteDate(startDate) || !isCompleteDate(endDate)) return

  const start = `${startDate.yyyy}-${startDate.mm}-${startDate.dd}`
  const end = `${endDate.yyyy}-${endDate.mm}-${endDate.dd}`

  params.append("startDate", start)
  params.append("endDate", end)
}

const buildQueryString = (values: any): string => {
  const params = new URLSearchParams()

  buildDateParams(values.dateType, values.startDate, values.endDate, params)

  const flags = buildFlagState(values.flags || [])
  if (flags.manualClockin) {
    params.append("origin", flags.manualClockin)
  }
  if (flags.outsideGeofence) {
    params.append("geofenceStatus", flags.outsideGeofence)
  }
  params.append("lateClockin", String(flags.lateClockin))
  params.append("earlyClockout", String(flags.earlyClockout))
  params.append("overtime", String(flags.overtime))

  if (values.departments?.length) {
    params.append("departmentId", values.departments.join(","))
  }
  if (values.shiftPlan?.length) {
    params.append("shiftPlanId", values.shiftPlan.join(","))
  }
  if (values.shiftPeriod?.length) {
    params.append("shiftPeriodId", values.shiftPeriod.join(","))
  }

  if (values.employees) {
    params.append("employeeId", values.employees.join(","))
  }

  return params.toString()
}

// ─── Radio Option ─────────────────────────────────────────────────────────────

interface RadioOptionProps {
  value: string
  label: string
  selectedValue: string
}

const RadioOption: FC<RadioOptionProps> = ({ value, label, selectedValue }) => (
  <Radio
    value={value}
    className={`text-slate-900 text-sm border p-2 w-fit rounded-md
      [&_.ant-radio-inner]:border-slate-400
      [&_.ant-radio-checked_.ant-radio-inner]:bg-slate-900
      [&_.ant-radio-checked_.ant-radio-inner]:border-slate-900
      ${selectedValue === value ? "border-slate-900" : "border-[#E2E8F0]"}`}
    style={{ display: "flex", alignItems: "center" }}
  >
    {label}
  </Radio>
)

// ─── Selected chips (rendered below department / employee selects) ───────────

interface SelectedChipsProps {
  ids: number[]
  options: { value: number | undefined; label: string | undefined }[]
  onRemove: (id: number) => void
}

const SelectedChips: FC<SelectedChipsProps> = ({ ids, options, onRemove }) => {
  if (!ids?.length) return null
  return (
    <div className="mt-2">
      <p className="text-xs text-slate-500 m-0">Show only:</p>
      <div className="flex flex-wrap gap-2 mt-1">
        {ids.map((id) => {
          const label = options.find((o) => o.value === id)?.label ?? `#${id}`
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 border border-slate-200 rounded-full px-3 py-1 text-xs text-slate-900"
            >
              {label}
              <button
                type="button"
                onClick={() => onRemove(id)}
                className="text-slate-500 hover:text-slate-900"
                aria-label="Remove"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          )
        })}
      </div>
    </div>
  )
}

// ─── Section ──────────────────────────────────────────────────────────────────

interface SectionProps {
  title: string
  children: React.ReactNode
  divider?: boolean
}

const Section: FC<SectionProps> = ({ title, children, divider = true }) => (
  <>
    <div>
      <p className="text-padeBlack font-semibold pb-2">{title}</p>
      {children}
    </div>
    {divider && (
      <Divider
        className="bg-gray-100"
        style={{ margin: "10px 0" }}
      />
    )}
  </>
)

const parseQueryStringToFormValues = (queryString: string) => {
  const params = new URLSearchParams(queryString)

  const flags: string[] = []
  if (params.get("origin") === "manual") flags.push("Manual Entry")
  if (params.get("geofenceStatus") === "outside") flags.push("outside geofence")
  if (params.get("lateClockin") === "true") flags.push("Late Clock-In")
  if (params.get("earlyClockout") === "true") flags.push("early clock in")
  if (params.get("overtime") === "true") flags.push("overtime")

  const startDateRaw = params.get("startDate") // "yyyy-mm-dd"
  const endDateRaw = params.get("endDate")

  const parseDate = (raw: string | null) => {
    if (!raw) return { dd: "", mm: "", yyyy: "" }
    const [yyyy, mm, dd] = raw.split("-")
    return { dd, mm, yyyy }
  }

  const departments = params.get("departmentId")
    ? params.get("departmentId")!.split(",").map(Number)
    : []

  const employees = params.get("employeeId")
    ? params.get("employeeId")!.split(",").map(Number)
    : []

  return {
    dateType: startDateRaw ? "custom_range" : "all_time",
    startDate: parseDate(startDateRaw),
    endDate: parseDate(endDateRaw),
    flags,
    departments,
    employees,
    shiftPlan: params.get("shiftPlanId")?.split(",").filter(Boolean) ?? [],
    shiftPeriod: params.get("shiftPeriodId")?.split(",").filter(Boolean) ?? [],
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

const FilterDrawer: FC<FilterDrawerProps> = ({
  open,
  onClose,
  onApply,
  workplaceId,
  companyId,
  dutyLocationId,
  departmentId,
  form,
  isLoading,
  onReset,
  filterQuery,
}) => {
  const dateType = Form.useWatch("dateType", form)
  const selectedDrawerFilter = Form.useWatch("dateType", form) ?? "all_time"
  const selectedDepartments = Form.useWatch("departments", form) as
    | number[]
    | undefined
  const selectedFlags = Form.useWatch("flags", form) as string[] | undefined
  const selectedEmployees = Form.useWatch("employees", form) as
    | number[]
    | undefined
  const selectedShiftPlans = Form.useWatch("shiftPlan", form) as
    | string[]
    | undefined
  const selectedShiftPeriods = Form.useWatch("shiftPeriod", form) as
    | string[]
    | undefined
  const selectedStartDate = Form.useWatch("startDate", form)
  const selectedEndDate = Form.useWatch("endDate", form)
  const hasFilterSelected =
    (dateType === "custom_range" &&
      isCompleteDate(selectedStartDate) &&
      isCompleteDate(selectedEndDate)) ||
    !!selectedFlags?.length ||
    !!selectedDepartments?.length ||
    !!selectedShiftPlans?.length ||
    !!selectedShiftPeriods?.length ||
    !!selectedEmployees?.length

  // ─── Option data ───────────────────────────────────────────────────────────
  // Loaded from their own endpoints, not the timesheet response, so the
  // options don't shrink to the current page or the filter already applied.

  const { data: workplaceEmployeesRes, isInitialLoading: isLoadingEmployees } =
    useGet<IEnrolledEmployeeRes>({
      url: `/v1/time-attendance/workplaces/${workplaceId}/employees?limit=100`,
      instanceKey: "ewaAxiosInstance",
      enabled: open && !!workplaceId,
    })
  const workplaceEmployees = workplaceEmployeesRes?.data?.docs ?? []

  const { data: shiftPlansRes, isInitialLoading: isLoadingShiftPlans } =
    useGet<IShiftPlansRes>({
      url: `/v1/time-attendance/shift-plans?workplaceId=${workplaceId}&companyId=${companyId}${
        dutyLocationId ? `&dutylocationId=${dutyLocationId}` : ""
      }&page=1&limit=100`,
      instanceKey: "ewaAxiosInstance",
      enabled: open && !!workplaceId,
    })

  // Shift periods belong to a shift plan, so they're loaded for the picked plans.
  const { data: shiftPeriods = [], isInitialLoading: isLoadingShiftPeriods } =
    useQuery({
      queryKey: [
        "/v1/time-attendance/shift-periods",
        ...(selectedShiftPlans ?? []),
      ],
      queryFn: async () => {
        const responses = await Promise.all(
          (selectedShiftPlans ?? []).map((shiftPlanId) =>
            ewaAxiosInstance.get<IShiftPeriodsRes>(
              `/v1/time-attendance/shift-periods?page=1&limit=50&shiftPlanId=${shiftPlanId}`
            )
          )
        )
        return responses.flatMap((res) => res.data?.data?.docs ?? [])
      },
      enabled: !!selectedShiftPlans?.length,
      refetchOnWindowFocus: false,
    })

  const employeeOptions = workplaceEmployees.map((employee) => ({
    value: employee.id,
    label: `${employee.firstName} ${employee.lastName}`,
  }))

  const departmentSelectOptions = Array.from(
    new Map(
      workplaceEmployees
        .filter((employee) => employee.department)
        .map((employee) => [employee.department.id, employee.department.name])
    ),
    ([value, label]) => ({ value, label })
  )

  const shiftPlanOptions = (shiftPlansRes?.data?.docs ?? []).map((plan) => ({
    value: String(plan.id),
    label: plan.title,
  }))

  const shiftPeriodOptions = shiftPeriods.map((period) => ({
    value: String(period.id),
    label: period.title,
  }))

  // Drop picked shift periods whose shift plan was just unpicked.
  const onValuesChange = (changed: { shiftPlan?: string[] }) => {
    if (!("shiftPlan" in changed)) return
    const shiftPlanIds = changed.shiftPlan ?? []
    form.setFieldValue(
      "shiftPeriod",
      (selectedShiftPeriods ?? []).filter((shiftPeriodId) =>
        shiftPeriods.some(
          (period) =>
            String(period.id) === shiftPeriodId &&
            shiftPlanIds.includes(String(period.shiftPlanId))
        )
      )
    )
  }

  const handleReset = () => {
    onReset()
    form.resetFields()
    onClose()
  }

  const handleApply = () => {
    form.submit()
  }

  const handleFinish = (values: any) => {
    const queryString = buildQueryString(values)
    onApply(queryString)
    onClose()
  }

  return (
    <Drawer
      rootClassName="ta-scope"
      width={444}
      closeIcon={null}
      open={open}
      onClose={onClose}
      styles={{
        header: { padding: 16 },
        body: { padding: 16 },
        footer: { padding: 16 },
      }}
      afterOpenChange={(isOpen) => {
        if (isOpen && filterQuery) {
          form.setFieldsValue(parseQueryStringToFormValues(filterQuery))
        } else if (isOpen && !filterQuery) {
          form.resetFields()
        }
      }}
      title={
        <div className="text-base font-bold text-slate-900 flex justify-between items-center gap-2">
          Filter
          <XMarkIcon
            className="text-[#111827] w-5"
            role="button"
            onClick={onClose}
          />
        </div>
      }
      footer={
        <div className="flex justify-between items-center gap-2">
          <Button
            style={{ width: "100%" }}
            className="border-2 border-slate-300"
            onClick={handleReset}
          >
            Reset Filter
          </Button>
          <Button
            color="primary"
            style={{ width: "100%" }}
            className="rounded-md"
            onClick={handleApply}
            disabled={isLoading || !hasFilterSelected}
          >
            Apply Filters
          </Button>
        </div>
      }
    >
      <LoadingRegion
        spinning={isLoadingEmployees || isLoadingShiftPlans}
        keepMounted
        placeholder={
          <div
            className="flex flex-col gap-4"
            aria-busy="true"
          >
            {/* date range, flags, department, shift plan, shift period, employee */}
            {["72px", "96px", "64px", "64px", "64px", "64px"].map(
              (height, i) => (
                <AppSkeleton
                  key={i}
                  variant="block"
                  width="100%"
                  height={height}
                />
              )
            )}
          </div>
        }
      >
        <Form
          form={form}
          onFinish={handleFinish}
          onValuesChange={onValuesChange}
        >
          {/* Date Range */}
          <Section title="Date range">
            <Form.Item
              name="dateType"
              initialValue="all_time"
            >
              <Radio.Group className="flex gap-1">
                <RadioOption
                  value="all_time"
                  label="All time"
                  selectedValue={selectedDrawerFilter}
                />
                <RadioOption
                  value="custom_range"
                  label="Custom range"
                  selectedValue={selectedDrawerFilter}
                />
              </Radio.Group>
            </Form.Item>

            {dateType === "custom_range" && (
              <div className="grid grid-cols-2 items-center mb-2">
                <span className="text-xs text-slate-500">
                  Start date:
                  <Form.Item
                    name="startDate"
                    initialValue={{ dd: "", mm: "", yyyy: "" }}
                  >
                    <DateInput />
                  </Form.Item>
                </span>
                <span className="text-xs text-slate-500">
                  End date:
                  <Form.Item
                    name="endDate"
                    initialValue={{ dd: "", mm: "", yyyy: "" }}
                  >
                    <DateInput />
                  </Form.Item>
                </span>
              </div>
            )}
          </Section>

          {/* Flags */}
          <Section title="Flags">
            <Form.Item name="flags">
              <Checkbox.Group className="flex flex-wrap gap-2">
                {FLAG_OPTIONS.map(({ name, value, key }) => (
                  <FilterFlags
                    key={key}
                    name={name}
                    value={value}
                  />
                ))}
              </Checkbox.Group>
            </Form.Item>
          </Section>

          {/* Filter by Department */}
          <Section title="Filter by department">
            <div className="relative [&_.ant-select-selector]:!pl-9 [&_.ant-select-selection-placeholder]:!left-9">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-950 z-10 pointer-events-none" />
              <Form.Item name="departments">
                <Select
                  mode="multiple"
                  placeholder="Search for a department"
                  options={departmentSelectOptions}
                  tagRender={() => <></>}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
            <SelectedChips
              ids={(selectedDepartments ?? []) as number[]}
              options={departmentSelectOptions}
              onRemove={(id) =>
                form.setFieldsValue({
                  departments: (selectedDepartments ?? []).filter(
                    (x) => x !== id
                  ),
                })
              }
            />
          </Section>

          {/* Filter by Shift Plan */}
          <Section title="Filter by shift plan">
            <Form.Item name="shiftPlan">
              <Select
                mode="multiple"
                placeholder="Search for a shift plan"
                options={shiftPlanOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </Section>

          {/* Filter by Shift Period */}
          <Section title="Filter by shift period">
            <Form.Item name="shiftPeriod">
              <Select
                mode="multiple"
                placeholder={
                  selectedShiftPlans?.length
                    ? "Search for a shift period"
                    : "Select a shift plan"
                }
                options={shiftPeriodOptions}
                loading={isLoadingShiftPeriods}
                optionFilterProp="label"
                disabled={!selectedShiftPlans?.length}
              />
            </Form.Item>
          </Section>

          {/* Filter by Employee */}
          <Section
            title="Filter by employee"
            divider={false}
          >
            <div className="relative [&_.ant-select-selector]:!pl-9 [&_.ant-select-selection-placeholder]:!left-9">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-950 z-10 pointer-events-none" />
              <Form.Item name="employees">
                <Select
                  mode="multiple"
                  placeholder="Search for an employee"
                  options={employeeOptions}
                  tagRender={() => <></>}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
            <SelectedChips
              ids={(selectedEmployees ?? []) as number[]}
              options={employeeOptions}
              onRemove={(id) =>
                form.setFieldsValue({
                  employees: (selectedEmployees ?? []).filter((x) => x !== id),
                })
              }
            />
          </Section>
        </Form>
      </LoadingRegion>
    </Drawer>
  )
}

export default FilterDrawer

const FilterFlags = ({
  selectedFlagFilter,
  name,
  value,
}: {
  selectedFlagFilter?: string[]
  name: string
  value: string
}) => {
  return (
    <Checkbox
      value={value}
      className={`text-slate-900 text-sm border px-2 py-1 w-fit rounded-full ${
        selectedFlagFilter?.includes(value)
          ? "border-slate-900"
          : "border-[#E2E8F0]"
      }`}
      style={{ display: "flex", alignItems: "center" }}
    >
      <span>{name}</span>
    </Checkbox>
  )
}
